// src/components/Customer/JobDetails.js
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { ArrowLeft, MapPin, CheckCircle2, X } from "lucide-react";
import { useToast } from "../../contexts/ToastContext";

import { auth, db } from "../../firebase";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  where,
} from "firebase/firestore";

import useCategories from "../../hooks/useCategories";

function safeString(v, fallback = "") {
  return typeof v === "string" ? v : fallback;
}
function safeNumber(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}
function isValidLatLng(p) {
  const lat = Number(p?.lat);
  const lng = Number(p?.lng);
  return Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180;
}

// Haversine distance (km)
function toRad(d) {
  return (Number(d) * Math.PI) / 180;
}
function haversineKm(a, b) {
  if (!isValidLatLng(a) || !isValidLatLng(b)) return null;
  const R = 6371;
  const dLat = toRad(Number(b.lat) - Number(a.lat));
  const dLng = toRad(Number(b.lng) - Number(a.lng));
  const lat1 = toRad(Number(a.lat));
  const lat2 = toRad(Number(b.lat));
  const s =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.sin(dLng / 2) * Math.sin(dLng / 2) * Math.cos(lat1) * Math.cos(lat2);
  const c = 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
  const km = R * c;
  return Number.isFinite(km) ? km : null;
}

function toYMD(d) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}
function toHM(d) {
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
}
function combineDateTime(dateStr, timeStr) {
  if (!dateStr || !timeStr) return null;
  const [y, m, d] = dateStr.split("-").map(Number);
  const [hh, mm] = timeStr.split(":").map(Number);
  if (![y, m, d, hh, mm].every(Number.isFinite)) return null;
  return new Date(y, m - 1, d, hh, mm, 0, 0);
}

function getOfferingSubIds(off) {
  const a = off?.subcategoryIds;
  if (Array.isArray(a)) return a.filter(Boolean).map(String);

  const b = off?.subcategories;
  if (Array.isArray(b)) return b.filter(Boolean).map(String);

  const c = off?.subcategoryId;
  if (typeof c === "string" && c) return [c];

  return [];
}

function Toast({ open, message, type = "success", onClose }) {
  if (!open) return null;

  return (
    <div className="fixed top-4 left-1/2 -translate-x-1/2 z-60 px-4 w-full max-w-md">
      <div
        className={`w-full rounded-xl border shadow-lg px-4 py-3 flex items-start gap-3 bg-white ${
          type === "success" ? "border-emerald-200" : "border-red-200"
        }`}
        role="status"
        aria-live="polite"
      >
        <div
          className={`mt-0.5 w-2.5 h-2.5 rounded-full ${
            type === "success" ? "bg-emerald-500" : "bg-red-500"
          }`}
        />
        <div className="flex-1">
          <p className="text-sm text-gray-900">{message}</p>
        </div>

        <button
          type="button"
          onClick={onClose}
          className="text-gray-400 hover:text-gray-700 transition-colors"
          aria-label="Close"
        >
          <X size={18} />
        </button>
      </div>
    </div>
  );
}

export default function JobDetails() {
  const navigate = useNavigate();
  const { taskerId } = useParams();
  const { showToast } = useToast();   // global

  // selectedAddressId from route state OR localStorage (supports refresh)
  const location = useLocation();
  const selectedAddressId =
    location.state?.selectedAddressId || localStorage.getItem("selectedAddressId") || "";

  const { categories = [], loading: categoriesLoading, error: categoriesError } = useCategories();

  // ✅ top toast
  const [toast, setToast] = useState({ open: false, message: "", type: "success" });
  useEffect(() => {
    if (!toast.open) return;
    const t = setTimeout(() => setToast((p) => ({ ...p, open: false })), 3000);
    return () => clearTimeout(t);
  }, [toast.open]);

  // ---------------------------
  // Load customer (name)
  // ---------------------------
  const [customerName, setCustomerName] = useState("");
  const [customerLoading, setCustomerLoading] = useState(true);
  

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        setCustomerLoading(true);
        const user = auth.currentUser;
        if (!user) {
          if (!cancelled) setCustomerName("");
          return;
        }

        const snap = await getDoc(doc(db, "customers", user.uid));
        const nameFromDoc = snap.exists() ? safeString(snap.data()?.name, "") : "";

        const fallback =
          safeString(user.displayName, "") ||
          safeString(user.email, "") ||
          "Customer";

        if (!cancelled) setCustomerName(nameFromDoc || fallback);
      } catch (e) {
        console.error("Load customer error:", e);
        const user = auth.currentUser;
        if (!cancelled) setCustomerName(safeString(user?.displayName, "") || "Customer");
      } finally {
        if (!cancelled) setCustomerLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // ---------------------------
  // Load tasker (categories/subcategories + location for distance)
  // ---------------------------
  const [tasker, setTasker] = useState(null);
  const [taskerLoading, setTaskerLoading] = useState(true);
  const [taskerError, setTaskerError] = useState("");

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        setTaskerLoading(true);
        setTaskerError("");

        if (!taskerId) throw new Error("Missing taskerId in URL");

        const snap = await getDoc(doc(db, "taskers", taskerId));
        if (!snap.exists()) throw new Error("Tasker not found");

        const data = snap.data() || {};
        const loc = data.location && typeof data.location === "object" ? data.location : null;

        const t = {
          id: snap.id,
          name: safeString(data.name, ""),
          categories: Array.isArray(data.categories) ? data.categories : [],
          subcategories: Array.isArray(data.subcategories) ? data.subcategories : [],
          location: loc ? { lat: safeNumber(loc.lat, null), lng: safeNumber(loc.lng, null) } : null,
        };

        if (!cancelled) setTasker(t);
      } catch (e) {
        console.error("Load tasker error:", e);
        if (!cancelled) setTaskerError(e?.message || "Failed to load tasker.");
        if (!cancelled) setTasker(null);
      } finally {
        if (!cancelled) setTaskerLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [taskerId]);

  // ---------------------------
  // Load customer selected address (fallback to default)
  // ---------------------------
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [addressLoading, setAddressLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        setAddressLoading(true);

        const user = auth.currentUser;
        if (!user) {
          if (!cancelled) setSelectedAddress(null);
          return;
        }

        // 1) Try selected address first
        if (selectedAddressId) {
          const selSnap = await getDoc(doc(db, "customers", user.uid, "savedAddresses", selectedAddressId));
          if (selSnap.exists()) {
            const data = selSnap.data() || {};
            const loc = data.location && typeof data.location === "object" ? data.location : null;

            if (!cancelled) {
              setSelectedAddress({
                id: selSnap.id,
                type: safeString(data.type, "Address"),
                addressText: safeString(data.addressText, ""),
                location: loc ? { lat: safeNumber(loc.lat, null), lng: safeNumber(loc.lng, null) } : null,
              });
            }
            return;
          }
        }

        // 2) Fallback to default address
        const q1 = query(
          collection(db, "customers", user.uid, "savedAddresses"),
          where("isDefault", "==", true),
          orderBy("createdAt", "desc"),
          limit(1)
        );

        const snap1 = await getDocs(q1);
        const doc0 = snap1.docs[0];

        if (!doc0) {
          if (!cancelled) setSelectedAddress(null);
          return;
        }

        const data = doc0.data() || {};
        const loc = data.location && typeof data.location === "object" ? data.location : null;

        if (!cancelled) {
          setSelectedAddress({
            id: doc0.id,
            type: safeString(data.type, "Address"),
            addressText: safeString(data.addressText, ""),
            location: loc ? { lat: safeNumber(loc.lat, null), lng: safeNumber(loc.lng, null) } : null,
          });
        }
      } catch (e) {
        console.error("Load address error:", e);
        if (!cancelled) setSelectedAddress(null);
      } finally {
        if (!cancelled) setAddressLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedAddressId]);

  // ---------------------------
  // Form state
  // ---------------------------
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [selectedSubcategoryId, setSelectedSubcategoryId] = useState("");
  const [selectedOfferingId, setSelectedOfferingId] = useState("");

  const [notes, setNotes] = useState(""); // optional

  const nowPlus1h = useMemo(() => new Date(Date.now() + 60 * 60 * 1000), []);
  const [date, setDate] = useState(toYMD(nowPlus1h));
  const [time, setTime] = useState(toHM(nowPlus1h));

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const inputBase =
    "w-full px-4 py-3 border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#006C35]";
  const inputErr = "border-red-500 focus:ring-red-200";
  const inputOk = "border-gray-300";

  const getCategoryMeta = (categoryId) => (categories || []).find((c) => c.id === categoryId) || null;

  // 1) Category dropdown from tasker.categories
  /* const categoryOptions = useMemo(() => {
    const ids = Array.isArray(tasker?.categories) ? tasker.categories : [];
    return ids
      .filter(Boolean)
      .map(String)
      .map((id) => ({ id, meta: getCategoryMeta(id) }))
      .sort((a, b) => (a.meta?.name || a.id).localeCompare(b.meta?.name || b.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasker?.categories, categories]); */

  // 2) Service dropdown from tasker.subcategories, filtered by selected category
  const subcategoryOptions = useMemo(() => {
    if (!selectedCategoryId) return [];
    const cat = getCategoryMeta(selectedCategoryId);
    const allowed = new Set((cat?.subcategories || []).map((s) => String(s.id)));

    const ids = Array.isArray(tasker?.subcategories) ? tasker.subcategories : [];
    const filtered = ids
      .filter(Boolean)
      .map(String)
      .filter((sid) => allowed.has(sid));

    return filtered
      .map((sid) => {
        const sub = cat?.subcategories?.find((s) => String(s.id) === sid);
        return { id: sid, name: safeString(sub?.name, sid) };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasker?.subcategories, selectedCategoryId, categories]);

  // 3) Offerings dropdown from serviceOfferings matching taskerId + category + subcategory
  const [offerings, setOfferings] = useState([]);
  const [offeringsLoading, setOfferingsLoading] = useState(false);
  const [offeringsError, setOfferingsError] = useState("");

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        setOfferingsError("");
        setOfferings([]);
        setSelectedOfferingId("");

        if (!taskerId || !selectedCategoryId || !selectedSubcategoryId) return;

        setOfferingsLoading(true);

        const q2 = query(
          collection(db, "serviceOfferings"),
          where("taskerId", "==", taskerId),
          where("categoryId", "==", selectedCategoryId)
        );

        const snap = await getDocs(q2);

        const list = snap.docs
          .map((d) => {
            const data = d.data() || {};
            const subIds = getOfferingSubIds(data);

            const price =
              data?.price != null
                ? safeNumber(data.price, 0)
                : data?.priceRange?.min != null
                ? safeNumber(data.priceRange.min, 0)
                : safeNumber(data?.priceMin, 0);

            return {
              id: d.id,
              categoryId: safeString(data.categoryId, ""),
              subcategoryIds: subIds,
              title: safeString(data.title, ""),
              description: safeString(data.description, ""),
              price,
            };
          })
          .filter((o) => o.subcategoryIds.includes(String(selectedSubcategoryId)));

        if (!cancelled) setOfferings(list);
      } catch (e) {
        console.error("Load offerings error:", e);
        if (!cancelled) setOfferingsError(e?.message || "Failed to load offerings.");
      } finally {
        if (!cancelled) setOfferingsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [taskerId, selectedCategoryId, selectedSubcategoryId]);

  const selectedOffering = useMemo(() => {
    if (!selectedOfferingId) return null;
    return offerings.find((o) => o.id === selectedOfferingId) || null;
  }, [offerings, selectedOfferingId]);

  // compute distance between customer selected address and tasker
  const distanceKm = useMemo(() => {
    const a = selectedAddress?.location;
    const b = tasker?.location;
    return haversineKm(a, b);
  }, [selectedAddress?.location, tasker?.location]);

  // rounded to 2 decimals for Firestore
  const distanceRounded2 = useMemo(() => {
    if (distanceKm == null) return null;
    return Math.round(distanceKm * 10) / 10;
  }, [distanceKm]);

  // Date/time rules
  const minDate = useMemo(() => toYMD(nowPlus1h), [nowPlus1h]);
  const minTimeForSelectedDate = useMemo(() => {
    if (date === minDate) return toHM(nowPlus1h);
    return "00:00";
  }, [date, minDate, nowPlus1h]);

  const validate = () => {
    const next = {};

    if (!selectedCategoryId) next.categoryId = "this category is mandatory";
    if (!selectedSubcategoryId) next.subcategoryId = "this service is mandatory";
    if (!selectedOfferingId) next.offeringId = "this offering is mandatory";

    if (!selectedAddress?.id) next.location = "this location is mandatory";

    const dt = combineDateTime(date, time);
    if (!safeString(date).trim()) next.preferredDate = "this preferred date is mandatory";
    if (!safeString(time).trim()) next.preferredTime = "this preferred time is mandatory";
    if (!dt) next.preferredAt = "this date & time is mandatory";
    else {
      const min = new Date(Date.now() + 60 * 60 * 1000);
      if (dt.getTime() < min.getTime()) next.preferredAt = "date & time must be at least 1 hour from now";
    }

    if (distanceRounded2 == null) next.distance = "cannot calculate distance (missing locations)";

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    const user = auth.currentUser;
    if (!user) {
      setErrors((p) => ({ ...p, auth: "this login is mandatory" }));
      setToast({ open: true, message: "Not authenticated.", type: "error" });
      return;
    }

    if (!validate()) {
      setToast({ open: true, message: "Please fill all mandatory fields.", type: "error" });
      return;
    }
    if (!taskerId) return;

    setSubmitting(true);
    try {
      const locObj = selectedAddress?.location || null;

      const jobRef = await addDoc(collection(db, "jobs"), {
        status: "draft",
        
        taskerId,
        customerId: user.uid,
        customerName: safeString(customerName, "Customer"),

        categoryId: selectedCategoryId,
        subcategoryId: selectedSubcategoryId,

        // keep price
        price: safeNumber(selectedOffering?.price, 0),

        // optional notes (remove if you want strict schema)
        notes: safeString(notes, ""),

        addressId: selectedAddress.id,
        locationText: safeString(selectedAddress.addressText, ""),
        location: {
          lat: isValidLatLng(locObj) ? Number(locObj.lat) : null,
          lng: isValidLatLng(locObj) ? Number(locObj.lng) : null,
        },

        distance: distanceRounded2, // ✅ stored, not shown

        preferredDate: date,
        preferredTime: time,
        preferredAt: combineDateTime(date, time),

        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

        showToast("Job created successfully!");
        navigate(`/customer/booking/job/${jobRef.id}`);
      
    } catch (err) {
      console.error("Create draft job error:", err);
      setToast({ open: true, message: err?.message || "Failed ❌ Please try again.", type: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  const catMeta = selectedCategoryId ? getCategoryMeta(selectedCategoryId) : null;
  const selectedSubMeta =
    catMeta?.subcategories?.find((s) => String(s.id) === String(selectedSubcategoryId)) || null;

  const offeringIncludes = useMemo(() => {
    if (!selectedOffering || !catMeta) return [];
    const subIds = selectedOffering.subcategoryIds || [];
    const subs = (catMeta.subcategories || []).filter((s) => subIds.includes(String(s.id)));
    return subs.map((s) => ({ id: String(s.id), name: safeString(s.name, String(s.id)) }));
  }, [selectedOffering, catMeta]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Toast
        open={toast.open}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((p) => ({ ...p, open: false }))}
      />

      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="text-gray-600 hover:text-gray-900" type="button">
            <ArrowLeft size={24} />
          </button>
          <h2 className="text-[#1E3A8A]">Job Details</h2>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-6">
        <form onSubmit={handleSubmit} className="bg-white rounded-lg p-6 shadow-sm space-y-6">
          {/* Loading/errors */}
          {taskerLoading ? <div className="text-sm text-gray-500">Loading tasker...</div> : null}
          {taskerError ? <div className="text-sm text-red-600">{taskerError}</div> : null}
          {categoriesLoading ? <div className="text-sm text-gray-500">Loading categories...</div> : null}
          {categoriesError ? <div className="text-sm text-red-600">{categoriesError}</div> : null}

          {/* 1) Category */}
          <div>
            <label className="block text-gray-700 mb-2">Select Category</label>
            <select
              value={selectedCategoryId}
              onChange={(e) => {
                const v = e.target.value;
                setSelectedCategoryId(v);
                setSelectedSubcategoryId("");
                setSelectedOfferingId("");
                setErrors((p) => {
                  const copy = { ...p };
                  delete copy.categoryId;
                  delete copy.subcategoryId;
                  delete copy.offeringId;
                  return copy;
                });
              }}
              className={`${inputBase} ${errors.categoryId ? inputErr : inputOk}`}
              disabled={taskerLoading || !!taskerError}
            >
              <option value="">Select category...</option>
              {(Array.isArray(tasker?.categories) ? tasker.categories : [])
                .filter(Boolean)
                .map(String)
                .sort()
                .map((id) => {
                  const meta = getCategoryMeta(id);
                  return (
                    <option key={id} value={id}>
                      {(meta?.icon ? `${meta.icon} ` : "") + (meta?.name || id)}
                    </option>
                  );
                })}
            </select>
            {errors.categoryId ? <p className="text-xs text-red-600 mt-1">{errors.categoryId}</p> : null}
          </div>

          {/* 2) Service */}
          <div>
            <label className="block text-gray-700 mb-2">Select Service</label>
            <select
              value={selectedSubcategoryId}
              onChange={(e) => {
                const v = e.target.value;
                setSelectedSubcategoryId(v);
                setSelectedOfferingId("");
                setErrors((p) => {
                  const copy = { ...p };
                  delete copy.subcategoryId;
                  delete copy.offeringId;
                  return copy;
                });
              }}
              className={`${inputBase} ${errors.subcategoryId ? inputErr : inputOk}`}
              disabled={!selectedCategoryId}
            >
              <option value="">{selectedCategoryId ? "Select service..." : "Select category first..."}</option>
              {subcategoryOptions.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
            {errors.subcategoryId ? <p className="text-xs text-red-600 mt-1">{errors.subcategoryId}</p> : null}
          </div>

          {/* 3) Offering */}
          <div>
            <label className="block text-gray-700 mb-2">Select Offering</label>
            <select
              value={selectedOfferingId}
              onChange={(e) => {
                setSelectedOfferingId(e.target.value);
                setErrors((p) => {
                  const copy = { ...p };
                  delete copy.offeringId;
                  return copy;
                });
              }}
              className={`${inputBase} ${errors.offeringId ? inputErr : inputOk}`}
              disabled={!selectedCategoryId || !selectedSubcategoryId || offeringsLoading}
            >
              <option value="">
                {!selectedSubcategoryId
                  ? "Select category & service first..."
                  : offeringsLoading
                  ? "Loading offerings..."
                  : offerings.length
                  ? "Select offering..."
                  : "No offerings available"}
              </option>

              {offerings.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.title || "Offering"} — {safeNumber(o.price, 0)} SAR
                </option>
              ))}
            </select>
            {offeringsError ? <p className="text-xs text-red-600 mt-1">{offeringsError}</p> : null}
            {errors.offeringId ? <p className="text-xs text-red-600 mt-1">{errors.offeringId}</p> : null}
          </div>

          {/* Details (read-only) */}
          {selectedOffering ? (
            <>
              <div className="border border-gray-200 rounded-lg p-4 bg-white">
                <h3 className="text-[#1E3A8A] font-semibold">{selectedOffering.title || "Service Title"}</h3>
                <p className="text-sm text-gray-600 mt-2">
                  {selectedOffering.description?.trim() ? selectedOffering.description : "No description available."}
                </p>
              </div>

              <div className="border border-gray-200 rounded-lg p-4 bg-white">
                <h4 className="text-[#1E3A8A] font-semibold mb-3">Service Includes</h4>

                <div className="px-3 py-2 rounded-lg bg-blue-50 text-sm text-gray-800 flex items-center gap-2 mb-3">
                  <span className="font-medium">
                    {catMeta?.icon ? `${catMeta.icon} ` : ""}
                    {catMeta?.name || selectedCategoryId}
                  </span>
                  {selectedSubMeta?.name ? <span className="text-gray-500">• {selectedSubMeta.name}</span> : null}
                </div>

                <div className="flex flex-wrap gap-2">
                  {offeringIncludes.length ? (
                    offeringIncludes.map((s) => (
                      <span
                        key={s.id}
                        className="px-3 py-2 rounded-lg bg-green-50 text-[#006C35] text-sm border border-green-100"
                      >
                        {s.name}
                      </span>
                    ))
                  ) : (
                    <span className="text-sm text-gray-500">No sub-services listed.</span>
                  )}
                </div>
              </div>

              <div className="border border-green-100 bg-green-50 rounded-lg p-4">
                <div className="text-sm text-gray-600">Service Price</div>
                <div className="text-2xl font-semibold text-[#006C35]">{safeNumber(selectedOffering.price, 0)} SAR</div>
              </div>
            </>
          ) : null}

          {/* Location (read-only) */}
          <div>
            <label className="block text-gray-700 mb-2">Location</label>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                readOnly
                value={safeString(selectedAddress?.addressText, "")}
                className={`w-full pl-10 pr-4 py-3 border rounded-lg bg-gray-100 focus:outline-none ${
                  errors.location ? "border-red-500" : "border-gray-300"
                }`}
                placeholder={addressLoading ? "Loading selected address..." : "No selected address"}
              />
            </div>
            {errors.location ? <p className="text-xs text-red-600 mt-1">{errors.location}</p> : null}
            <p className="text-xs text-gray-500 mt-2">
              {selectedAddress?.id ? (
                <>
                  Using selected address:{" "}
                  <span className="font-medium">{safeString(selectedAddress.type, "Address")}</span>
                </>
              ) : (
                "No selected address found. Please select an address from Home first."
              )}
            </p>
          </div>

          {/* Notes (optional) */}
          <div>
            <label className="block text-gray-700 mb-2">Additional Notes (Optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Describe your issue in detail..."
              rows={4}
              className={`${inputBase} ${inputOk} resize-none`}
            />
          </div>

          {/* Date & Time */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-700 mb-2">Preferred Date</label>
              <input
                type="date"
                value={date}
                min={toYMD(nowPlus1h)}
                onChange={(e) => {
                  const v = e.target.value;
                  setDate(v);
                  if (v === toYMD(nowPlus1h) && time < minTimeForSelectedDate) setTime(minTimeForSelectedDate);
                  setErrors((p) => {
                    const copy = { ...p };
                    delete copy.preferredDate;
                    delete copy.preferredAt;
                    return copy;
                  });
                }}
                className={`${inputBase} ${errors.preferredDate || errors.preferredAt ? inputErr : inputOk}`}
              />
              {errors.preferredDate ? <p className="text-xs text-red-600 mt-1">{errors.preferredDate}</p> : null}
            </div>

            <div>
              <label className="block text-gray-700 mb-2">Preferred Time</label>
              <input
                type="time"
                value={time}
                min={minTimeForSelectedDate}
                onChange={(e) => {
                  setTime(e.target.value);
                  setErrors((p) => {
                    const copy = { ...p };
                    delete copy.preferredTime;
                    delete copy.preferredAt;
                    return copy;
                  });
                }}
                className={`${inputBase} ${errors.preferredTime || errors.preferredAt ? inputErr : inputOk}`}
              />
              <p className="text-xs text-gray-500 mt-2">Must be at least 1 hour from now.</p>
              {errors.preferredTime ? <p className="text-xs text-red-600 mt-1">{errors.preferredTime}</p> : null}
              {errors.preferredAt ? <p className="text-xs text-red-600 mt-1">{errors.preferredAt}</p> : null}
            </div>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={submitting || customerLoading}
            className="w-full bg-[#006C35] text-white py-4 rounded-lg hover:bg-[#005428] transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {submitting ? (
              "Saving draft..."
            ) : (
              <>
                <CheckCircle2 size={18} />
                Continue
              </>
            )}
          </button>

          {errors.auth ? <p className="text-sm text-red-600">{errors.auth}</p> : null}
          {errors.distance ? <p className="text-sm text-red-600">{errors.distance}</p> : null}
        </form>
      </div>
    </div>
  );
}